/* Карточка клиента (PRO) — как в прототипе (client.html): шапка — фото, имя, пол и возраст, «В конструктор» и
   «Календарь»; поля «подпись — значение» двумя группами: «Связь» (телефон, Telegram, Max) и «Тренировки» (группа со
   сменой, оплачено до с «Принять оплату», активность, с нами с); заметка тренера растёт по тексту. Ниже табы
   «Обзор» (замеры и рекорды) · «События» · «История оплат» (PAY-9), открытый таб — в адресе (#events, #pay).
   Программа за клиентом не закрепляется — это лишь способ разложить много тренировок разом (страница программы). */
import { trpc, useCatalog } from '@tg/app-core';
import { norm } from '@tg/domain';
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { lastActive, type ClientFull } from '../lib/data';
import { PMNAMES } from '../lib/dict';
import { GroupPick } from '../ui/GroupPick';
import { addDays, age, ago, daysBetween, dlong, dshort, fmtNum, humanDate, ini, parseNum, phoneView, plural, rub, today } from '../lib/format';
import { usePageTitle } from '../shell/Shell';
import { LineChart } from '../ui/charts';
import { FeedList, useEvents } from '../ui/Feed';
import { ICON } from '../ui/icons';
import { LimitAlert } from '../ui/LimitAlert';
import { Modal } from '../ui/Modal';
import { toast } from '../ui/Toast';

const TABS = [
  ['overview', 'Обзор'],
  ['events', 'События'],
  ['pay', 'История оплат'],
] as const;
type Tab = (typeof TABS)[number][0];
const tabOf = (h: string): Tab => (TABS.some((t) => t[0] === h) ? (h as Tab) : 'overview');
type MKey = 'weight' | 'waist' | 'chest' | 'hips' | 'fat';
const MEAS: [MKey, string, string][] = [
  ['weight', 'Вес', 'кг'],
  ['waist', 'Талия', 'см'],
  ['chest', 'Грудь', 'см'],
  ['hips', 'Бёдра', 'см'],
  ['fat', 'Жир', '%'],
];
/* ─── Замеры: таблица по датам и график по выбранному показателю ─── */
function Meas({ C }: { C: ClientFull }) {
  const [mk, setMk] = useState<MKey>('weight');
  const [add, setAdd] = useState(false);
  const rows = [...C.measurements].sort((a, b) => a.date.localeCompare(b.date));
  const [, name, unit] = MEAS.find((m) => m[0] === mk)!;
  const series = rows.filter((r) => r[mk] != null).map((r) => [r.date, r[mk]!] as [string, number]);
  const fmt = (k: MKey, v: number | null) => (v == null ? '—' : k === 'weight' || k === 'fat' ? v.toFixed(1).replace('.', ',') : String(Math.round(v)));
  const delta = (i: number, k: MKey) => {
    const cur = rows[i],
      prev = rows[i - 1];
    const d = !cur || !prev || cur[k] == null || prev[k] == null ? 0 : Math.round((cur[k]! - prev[k]!) * 10) / 10;
    return <i className={'dl ' + (d < 0 ? 'dn' : d > 0 ? 'up' : '')}>{d ? (d > 0 ? '+' : '') + fmtNum(d) : ''}</i>;
  };
  const a = age(C.born);
  return (
    <div className="card">
      <div className="mhead">
        <span className="mmeta">
          <span>
            <s>Рост</s>
            {C.height ? C.height + ' см' : '—'}
          </span>
          <span>
            <s>Дата рождения</s>
            {C.born ? dlong(C.born) : '—'}
          </span>
        </span>
        <span className="sp"></span>
        <button className="btn gh sm" id="addMeas" onClick={() => setAdd(true)}>
          <ICON.plus /> Добавить замер
        </button>
      </div>
      <div className="mbody">
        <div className="tblwrap">
          <table className="tbl ctbl mtbl">
            <thead>
              <tr>
                <th>Дата</th>
                {MEAS.map(([k, n, u]) => (
                  <th className="r" key={k}>
                    {n}
                    <s>{u}</s>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows
                .map((r, i) => ({ r, i }))
                .reverse()
                .map(({ r, i }) => (
                  <tr key={r.id}>
                    <td>{humanDate(r.date)}</td>
                    {MEAS.map(([k]) => (
                      <td className="r" key={k}>
                        <span className="mv">
                          <b>{fmt(k, r[k])}</b>
                          {delta(i, k)}
                        </span>
                      </td>
                    ))}
                  </tr>
                ))}
              {!rows.length && (
                <tr>
                  <td colSpan={6} className="mut">
                    Замеров пока нет
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <div className="mchart">
          <div className="pmpick">
            {MEAS.map(([k, n]) => (
              <button key={k} className={mk === k ? 'on' : ''} onClick={() => setMk(k)}>
                {n}
              </button>
            ))}
          </div>
          <div className="chartbox">
            {series.length > 1 ? (
              <LineChart series={series} unit={unit} />
            ) : (
              <div className="empty">
                <div className="t">Мало замеров для графика{name ? '' : ''}</div>
              </div>
            )}
          </div>
        </div>
      </div>
      {add && <AddMeas C={C} onClose={() => setAdd(false)} />}
    </div>
  );
}

function AddMeas({ C, onClose }: { C: ClientFull; onClose: () => void }) {
  const [v, setV] = useState<Record<string, string>>({});
  const utils = trpc.useUtils();
  const m = trpc.trainer.addMeasurement.useMutation({
    onSuccess: () => {
      toast('Замер записан');
      utils.trainer.client.invalidate({ id: C.id });
      onClose();
    },
    onError: (e) => toast(e.message || 'Не сохранилось'),
  });
  const save = () => {
    const r: Partial<Record<MKey, number>> = {};
    for (const [k] of MEAS) {
      const n = parseNum(v[k] || '');
      if (isFinite(n)) r[k] = n;
    }
    if (!Object.keys(r).length) return toast('Внесите хотя бы один показатель');
    m.mutate({ clientId: C.id, date: today(), ...r });
  };
  return (
    <Modal
      title="Новый замер"
      onClose={onClose}
      foot={
        <>
          <span className="sp"></span>
          <button className="btn gh" onClick={onClose}>
            Отмена
          </button>
          <button className="btn" id="nmSave" disabled={m.isPending} onClick={save}>
            Сохранить
          </button>
        </>
      }
    >
      <div className="cols3">
        {MEAS.map(([k, n, u], i) => (
          <div className="formfld" key={k}>
            <span className="k">
              {n}, {u}
            </span>
            <input className="inp" autoFocus={i === 0} placeholder="—" value={v[k] || ''} onChange={(e) => setV({ ...v, [k]: e.target.value })} onKeyDown={(e) => e.key === 'Enter' && save()} />
          </div>
        ))}
      </div>
    </Modal>
  );
}

/* ─── Рекорды (1ПМ/3ПМ, как в приложении клиента): правка прямо в таблице, динамика графиком ─── */
const PM_SHOW = 30;

function Maxes({ C }: { C: ClientFull }) {
  const cat = useCatalog();
  const utils = trpc.useUtils();
  const pmLabel = (k: string) => PMNAMES[k] || cat.ex(k)?.ru || k;
  const { keys, pm, pm3, pm3Row, hist } = useMemo(() => {
    const pm: Record<string, number> = {},
      pm3: Record<string, number> = {},
      pm3Row: Record<string, string> = {},
      hist: Record<string, [string, number][]> = {};
    /* Строки по умолчанию (DEFAULT_PM) стоят пустыми, пока их не убрали; упражнения с данными — всегда */
    const keys: string[] = [...C.pmKeys];
    for (const m of C.maxes) {
      if (m.reps === 1) {
        if (!keys.includes(m.key)) keys.push(m.key);
        pm[m.key] = m.kg;
        (hist[m.key] ||= []).push([m.date, m.kg]);
      } else if (m.reps === 3) {
        if (!keys.includes(m.key)) keys.push(m.key);
        pm3[m.key] = m.kg;
        pm3Row[m.key] = m.id;
      }
    }
    /* Порядок — как у ключей движений (присед, фронт. присед, становая…), потом остальные */
    const ord = Object.keys(PMNAMES);
    const rank = (k: string) => (ord.includes(k) ? ord.indexOf(k) : ord.length);
    keys.sort((a, b) => rank(a) - rank(b));
    return { keys, pm, pm3, pm3Row, hist };
  }, [C.maxes, C.pmKeys]);
  const [rm, setRm] = useState<string | null>(null);
  const pmRow = trpc.trainer.pmRow.useMutation({ onSuccess: () => done(), onError: (e) => toast(e.message || 'Не сохранилось') });
  const remove = (k: string) => pmRow.mutate({ clientId: C.id, key: k, on: false }, { onSuccess: () => (setRm(null), toast('Упражнение убрано из рекордов')) });
  const [metric, setMetric] = useState<string | null>(null);
  const [add, setAdd] = useState(false);
  /* Список тянется вниз без прокрутки внутри карточки; больше PM_SHOW — остальные по «Показать все» */
  const [all, setAll] = useState(false);
  const cur = metric && keys.includes(metric) ? metric : keys.find((k) => hist[k]?.length > 1) || keys.find((k) => pm[k] != null) || keys[0] || null;
  const lastAll = keys
    .map((k) => hist[k]?.slice(-1)[0]?.[0])
    .filter(Boolean)
    .sort()
    .pop();
  const done = () => {
    utils.trainer.client.invalidate({ id: C.id });
    utils.trainer.clients.invalidate();
  };
  const setMax = trpc.trainer.setMax.useMutation({ onSuccess: done, onError: (e) => toast(e.message || 'Не сохранилось') });
  const delMax = trpc.trainer.deleteMax.useMutation({ onSuccess: done });
  const commit1 = (k: string, raw: string) => {
    const v = parseNum(raw);
    if (!isFinite(v) || v <= 0 || v === pm[k]) return;
    setMax.mutate({ clientId: C.id, key: k, kg: v, reps: 1 }, { onSuccess: () => toast('1ПМ обновлён — проценты в тренировках пересчитаются') });
  };
  const commit3 = (k: string, raw: string) => {
    const v = parseNum(raw);
    if (!raw.trim() && pm3Row[k]) return delMax.mutate({ id: pm3Row[k] }, { onSuccess: () => toast('3ПМ убран') });
    if (!isFinite(v) || v <= 0 || v === pm3[k]) return;
    setMax.mutate({ clientId: C.id, key: k, kg: v, reps: 3 }, { onSuccess: () => toast('3ПМ записан') });
  };
  const series = cur && hist[cur] && hist[cur].length > 1 ? hist[cur] : null;
  const onKey = (e: React.KeyboardEvent<HTMLInputElement>) => e.key === 'Enter' && e.currentTarget.blur();
  return (
    <div className="card">
      <div className="mhead">
        <span className="mmeta">
          <span>
            <s>Упражнений</s>
            {keys.length}
          </span>
          <span>
            <s>Обновлено</s>
            {lastAll ? humanDate(lastAll) : '—'}
          </span>
        </span>
        <span className="sp"></span>
        <button className="btn gh sm" id="addPm" onClick={() => setAdd(true)}>
          <ICON.plus /> Добавить упражнение
        </button>
      </div>
      <div className="mbody">
        <div className="tblwrap">
          <table className="tbl ctbl pmt" data-tbl="pm">
            <thead>
              <tr>
                <th>Упражнение</th>
                <th className="r">
                  1ПМ<s>кг</s>
                </th>
                <th className="r">
                  3ПМ<s>кг</s>
                </th>
                <th className="r">Обновлён</th>
                <th className="pmrmh"></th>
              </tr>
            </thead>
            <tbody>
              {keys.length ? (
                (all ? keys : keys.slice(0, PM_SHOW)).map((k) => {
                  const last = hist[k]?.slice(-1)[0]?.[0];
                  return (
                    <tr key={k} className={'click ' + (cur === k ? 'on' : '')} onClick={(e) => !(e.target as Element).closest('input') && setMetric(k)}>
                      <td>
                        <b>{pmLabel(k)}</b>
                      </td>
                      <td className="r">
                        <input className="pmin" key={k + (pm[k] ?? '')} defaultValue={pm[k] != null ? fmtNum(pm[k]) : ''} placeholder="—" onBlur={(e) => commit1(k, e.target.value)} onKeyDown={onKey} />
                      </td>
                      <td className="r">
                        <input className="pmin" key={k + (pm3[k] ?? '')} defaultValue={pm3[k] != null ? fmtNum(pm3[k]) : ''} placeholder="—" onBlur={(e) => commit3(k, e.target.value)} onKeyDown={onKey} />
                      </td>
                      <td className="r mut">{last ? humanDate(last) : '—'}</td>
                      <td className="pmrm">
                        <button title="Убрать из рекордов" aria-label="Убрать из рекордов" onClick={() => (pm[k] != null || pm3[k] != null ? setRm(k) : remove(k))}>
                          <ICON.x />
                        </button>
                      </td>
                    </tr>
                  );
                })
              ) : null}
              {keys.length > PM_SHOW && (
                <tr className="pmmore">
                  <td colSpan={5}>
                    <button className={all ? 'on' : ''} onClick={() => setAll(!all)}>
                      {all ? 'Свернуть' : 'Показать все · ещё ' + (keys.length - PM_SHOW)}
                      <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.7">
                        <path d="M4 6l4 4 4-4" />
                      </svg>
                    </button>
                  </td>
                </tr>
              )}
              {keys.length ? null : (
                <tr>
                  <td colSpan={5} className="mut">
                    Рекордов пока нет — добавьте упражнение
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <div className="mchart">
          {cur && <div className="pmname">{pmLabel(cur)}</div>}
          <div className="chartbox">
            {series ? (
              <LineChart series={series} unit="кг" />
            ) : (
              <div className="empty">
                <div className="t">Истории пока нет</div>
                <div className="d">Появится после второго зафиксированного рекорда</div>
              </div>
            )}
          </div>
        </div>
      </div>
      {rm && (
        <Modal
          title="Убрать из рекордов?"
          onClose={() => setRm(null)}
          foot={
            <>
              <span className="sp"></span>
              <button className="btn gh" onClick={() => setRm(null)}>
                Отмена
              </button>
              <button className="btn" disabled={pmRow.isPending} onClick={() => remove(rm)}>
                Убрать
              </button>
            </>
          }
        >
          <p className="lead">«{pmLabel(rm)}» пропадёт из рекордов клиента вместе с историей. Веса от процентов в уже составленных тренировках останутся как есть.</p>
        </Modal>
      )}
      {add && (
        <AddPm
          C={C}
          have={new Set(keys)}
          onClose={() => setAdd(false)}
          onAdded={(k) => {
            setMetric(k);
            setAll(true);
            done();
          }}
        />
      )}
    </div>
  );
}

function AddPm({ C, have, onClose, onAdded }: { C: ClientFull; have: Set<string>; onClose: () => void; onAdded: (k: string) => void }) {
  const cat = useCatalog();
  /* Группы упражнений (EX-4): список кандидатов в рекорды, сгруппированный по группам упражнения — упражнение
     может быть сразу в нескольких, тогда оно попадает под каждую; можно отметить отдельные упражнения или
     сразу всю группу галочкой в заголовке */
  const byGroup = useMemo(() => {
    const seen = new Set(have);
    const out = new Map<string, [string, string][]>();
    for (const e of cat.list) {
      if (!e.units.includes('кг')) continue;
      const k = e.pm || e.id;
      if (seen.has(k)) continue;
      seen.add(k);
      for (const g of e.groups.length ? e.groups : ['Без группы']) (out.get(g) || out.set(g, []).get(g)!).push([k, PMNAMES[k] || e.ru]);
    }
    for (const list of out.values()) list.sort((a, b) => a[1].localeCompare(b[1], 'ru'));
    return [...out.entries()].sort(([a], [b]) => (a === 'Без группы' ? 1 : b === 'Без группы' ? -1 : a.localeCompare(b, 'ru')));
  }, [cat.list, have]);
  const [sel, setSel] = useState<Set<string>>(new Set());
  const [q, setQ] = useState('');
  const [closed, setClosed] = useState<Set<string>>(new Set());
  const shown = useMemo(() => {
    const n = norm(q);
    if (!n) return byGroup;
    return byGroup.map(([g, list]) => [g, list.filter(([, name]) => norm(name).includes(n))] as [string, [string, string][]]).filter(([, list]) => list.length);
  }, [byGroup, q]);
  const [v1, setV1] = useState('');
  const [v3, setV3] = useState('');
  const m = trpc.trainer.setMax.useMutation();
  const row = trpc.trainer.pmRow.useMutation();
  const toggle = (k: string) =>
    setSel((s) => {
      const next = new Set(s);
      next.has(k) ? next.delete(k) : next.add(k);
      return next;
    });
  const toggleGroup = (keys: string[], on: boolean) =>
    setSel((s) => {
      const next = new Set(s);
      keys.forEach((k) => (on ? next.add(k) : next.delete(k)));
      return next;
    });
  const pending = m.isPending || row.isPending;
  const save = async () => {
    if (!sel.size) return;
    try {
      if (sel.size > 1) {
        /* Несколько сразу — без веса, тренер или клиент внесут 1ПМ позже в таблице */
        await Promise.all([...sel].map((k) => row.mutateAsync({ clientId: C.id, key: k, on: true })));
        toast('Упражнения добавлены');
        onAdded([...sel][0]);
        return onClose();
      }
      const k = [...sel][0];
      const a = parseNum(v1),
        b = parseNum(v3);
      /* Без 1ПМ — пустая строка, клиент или тренер внесут позже */
      if (!(isFinite(a) && a > 0)) {
        await row.mutateAsync({ clientId: C.id, key: k, on: true });
        toast('Упражнение добавлено');
        onAdded(k);
        return onClose();
      }
      await m.mutateAsync({ clientId: C.id, key: k, kg: a, reps: 1 });
      if (isFinite(b) && b > 0) await m.mutateAsync({ clientId: C.id, key: k, kg: b, reps: 3 });
      toast('Упражнение добавлено');
      onAdded(k);
      onClose();
    } catch (e: any) {
      toast(e?.message || 'Не сохранилось');
    }
  };
  return (
    <Modal
      title="Новое упражнение в рекордах"
      onClose={onClose}
      foot={
        <>
          <span className="sp"></span>
          <button className="btn gh" onClick={onClose}>
            Отмена
          </button>
          <button className="btn" id="npSave" disabled={pending || !sel.size} onClick={save}>
            {sel.size > 1 ? `Добавить (${sel.size})` : 'Добавить'}
          </button>
        </>
      }
    >
      <div className="formfld">
        <span className="k">Упражнения</span>
        <label className="search pmg-search">
          <ICON.search />
          <input placeholder="Поиск упражнения…" autoFocus value={q} onChange={(e) => setQ(e.target.value)} />
        </label>
        <div className="pmaddlist">
          {shown.map(([exGroup, list]) => {
            const allOn = list.every(([k]) => sel.has(k));
            const isClosed = closed.has(exGroup);
            return (
              <div key={exGroup} className="pmg">
                <div className="pmg-h">
                  <label>
                    <input type="checkbox" checked={allOn} onChange={(e) => toggleGroup(list.map(([k]) => k), e.target.checked)} />
                    <b>{exGroup}</b>
                  </label>
                  <span className="n">{list.length}</span>
                  <button
                    type="button"
                    className="pmg-fold"
                    aria-label={isClosed ? 'Развернуть' : 'Свернуть'}
                    onClick={() =>
                      setClosed((s) => {
                        const next = new Set(s);
                        next.has(exGroup) ? next.delete(exGroup) : next.add(exGroup);
                        return next;
                      })
                    }
                  >
                    {isClosed ? '▸' : '▾'}
                  </button>
                </div>
                {!isClosed &&
                  list.map(([k, n]) => (
                    <label key={k} className="pmg-row">
                      <input type="checkbox" checked={sel.has(k)} onChange={() => toggle(k)} />
                      <span>{n}</span>
                    </label>
                  ))}
              </div>
            );
          })}
          {!shown.length && <div className="hint">Ничего не нашли</div>}
        </div>
      </div>
      {sel.size === 1 && (
        <div className="cols2" style={{ marginTop: 12 }}>
          <div className="formfld">
            <span className="k">1ПМ, кг · можно позже</span>
            <input className="inp" autoFocus placeholder="—" value={v1} onChange={(e) => setV1(e.target.value)} />
          </div>
          <div className="formfld">
            <span className="k">3ПМ, кг · необязательно</span>
            <input className="inp" placeholder="—" value={v3} onChange={(e) => setV3(e.target.value)} />
          </div>
        </div>
      )}
    </Modal>
  );
}

/* Заметка тренера: многострочная, растёт по высоте вместе с текстом; сохраняется при уходе из поля */
function NoteArea({ value, onSave }: { value: string; onSave: (v: string) => void }) {
  const ta = useRef<HTMLTextAreaElement>(null);
  const fit = () => {
    const t = ta.current;
    if (!t) return;
    t.style.height = 'auto';
    t.style.height = t.scrollHeight + 2 + 'px';
  };
  useLayoutEffect(fit, []);
  useEffect(() => {
    addEventListener('resize', fit);
    return () => removeEventListener('resize', fit);
  }, []);
  return (
    <textarea
      ref={ta}
      id="fNote"
      rows={1}
      defaultValue={value}
      placeholder="Что важно помнить об этом клиенте…"
      onInput={fit}
      onBlur={(e) => e.target.value.trim() !== value && onSave(e.target.value.trim())}
    />
  );
}

/* ─── Оплата занятий (PAY-9): сумма, срок, история платежей. Деньги клиент переводит тренеру сам ─── */
type FeeSt = 'none' | 'closed' | 'today' | 'tomorrow' | 'soon' | 'ok';
const feeState = (until: string | null, T: string): FeeSt => {
  if (!until) return 'none';
  const r = daysBetween(T, until);
  return r < 0 ? 'closed' : r === 0 ? 'today' : r === 1 ? 'tomorrow' : r <= 7 ? 'soon' : 'ok';
};
/** Месяц вперёд без последнего дня: с 15 октября — по 14 ноября */
const monthAfter = (from: string) => {
  const d = new Date(from + 'T00:00:00');
  const day = d.getDate();
  d.setMonth(d.getMonth() + 1);
  if (d.getDate() !== day) d.setDate(0);
  return addDays(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`, -1);
};
/** Новый срок после оплаты: без перерыва, если доступ ещё открыт; иначе — со дня оплаты */
const feeNext = (until: string | null, T: string) => {
  const from = until && until >= T ? addDays(until, 1) : T;
  return { from, until: monthAfter(from) };
};

function Pay({ C, onFee }: { C: ClientFull; onFee: (m: 'pay' | 'edit') => void }) {
  const T = today();
  const st = feeState(C.paidUntil, T);
  if (st === 'none')
    return (
      <div className="card">
        <div className="empty">
          <div className="t">Оплата не ведётся</div>
          <div className="d">Клиент тренируется без ограничений. Задайте сумму и срок — они появятся здесь и в списке клиентов, а лента напомнит, когда срок подойдёт к концу.</div>
          <button className="btn sm" style={{ marginTop: 12 }} onClick={() => onFee('edit')}>
            <ICON.plus /> Задать оплату
          </button>
        </div>
      </div>
    );
  const r = daysBetween(T, C.paidUntil!);
  const left = st === 'closed' ? 'истекла' : st === 'today' ? 'последний день' : st === 'tomorrow' ? 'завтра последний день' : `ещё ${r} ${plural(r, 'день', 'дня', 'дней')}`;
  return (
    <div className="card">
      <div className="mhead">
        <span className="mh">
          <s>Сумма</s>
          <b>{C.fee.amt != null ? rub(C.fee.amt) : '—'}</b>
        </span>
        <span className="mh">
          <s>Оплачено до</s>
          <b className={'fee-' + st}>
            {dlong(C.paidUntil!)} <i>· {left}</i>
          </b>
        </span>
        <span className="sp"></span>
        <button className="btn gh sm" onClick={() => onFee('edit')}>
          Изменить
        </button>
        <button className="btn sm" onClick={() => onFee('pay')}>
          <ICON.chk /> Получил оплату
        </button>
      </div>
      <div className="tblwrap">
        <table className="tbl ctbl">
          <thead>
            <tr>
              <th>Платёж получен</th>
              <th className="r">Сумма</th>
              <th>Оплачено до</th>
            </tr>
          </thead>
          <tbody>
            {C.fee.hist.length ? (
              C.fee.hist.map((h, i) => (
                <tr key={i}>
                  <td>{dlong(h.d)}</td>
                  <td className="r">
                    <b>{rub(h.amt)}</b>
                  </td>
                  <td>{dlong(h.until)}</td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={3} className="mut">
                  Платежей пока нет
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* «Получил оплату» — сумма и новая дата (по умолчанию месяц, без перерыва); «Задать / изменить» — без платежа */
function FeeModal({ C, mode, onClose }: { C: ClientFull; mode: 'pay' | 'edit'; onClose: () => void }) {
  const T = today();
  const pay = mode === 'pay';
  const st = feeState(C.paidUntil, T);
  const nx = feeNext(C.paidUntil, T);
  const [amt, setAmt] = useState(C.fee.amt != null ? String(C.fee.amt) : '');
  const [until, setUntil] = useState(pay ? nx.until : C.paidUntil || monthAfter(T));
  const utils = trpc.useUtils();
  const done = (msg: string) => {
    utils.trainer.client.invalidate({ id: C.id });
    utils.trainer.clients.invalidate();
    toast(msg);
    onClose();
  };
  const err = (e: { message?: string }) => toast(e.message || 'Не сохранилось');
  const receive = trpc.trainer.receiveFee.useMutation({ onSuccess: (_r, v) => done('Оплата записана — оплачено до ' + humanDate(v.until)), onError: err });
  const set = trpc.trainer.setFee.useMutation({ onSuccess: (_r, v) => done(v.until ? 'Оплата сохранена' : 'Оплата не ведётся'), onError: err });
  const save = () => {
    const a = parseNum(amt.replace(/\s/g, ''));
    if (!isFinite(a) || a <= 0) return toast('Укажите сумму');
    if (!until || (pay && until < T)) return toast('Укажите дату «оплачено до»');
    if (pay) receive.mutate({ clientId: C.id, amt: a, until });
    else set.mutate({ clientId: C.id, amt: a, until });
  };
  const hint = pay
    ? (st === 'closed' ? `Срок уже вышел — новый считается со дня оплаты, ${humanDate(T)}.` : `Новый срок идёт с ${humanDate(nx.from)}, без перерыва.`) + ' По умолчанию — месяц, дату можно поменять.'
    : 'Сумма и срок видны здесь и в списке клиентов; когда срок подойдёт к концу, лента напомнит.';
  return (
    <Modal
      title={(pay ? 'Получил оплату · ' : C.paidUntil ? 'Оплата занятий · ' : 'Задать оплату · ') + C.name}
      onClose={onClose}
      foot={
        <>
          {!pay && C.paidUntil && (
            <button className="lnk-rm" onClick={() => set.mutate({ clientId: C.id, amt: null, until: null })}>
              Не вести оплату
            </button>
          )}
          <span className="sp"></span>
          <button className="btn gh" onClick={onClose}>
            Отмена
          </button>
          <button className="btn" disabled={receive.isPending || set.isPending} onClick={save}>
            {pay ? 'Получил оплату' : 'Сохранить'}
          </button>
        </>
      }
    >
      <div className="cols2">
        <div className="formfld">
          <span className="k">Сумма, ₽</span>
          <input className="inp" inputMode="numeric" autoFocus value={amt} placeholder="Например: 6000" onChange={(e) => setAmt(e.target.value)} />
        </div>
        <div className="formfld">
          <span className="k">Оплачено до</span>
          <input className="inp" type="date" value={until} onChange={(e) => setUntil(e.target.value)} />
        </div>
      </div>
      <div className="hint">{hint}</div>
    </Modal>
  );
}

/* Дата (в часовом поясе браузера) из Date/строки — для «заморожен с» */
const isoDay = (d: Date | string) => {
  const x = new Date(d);
  return `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, '0')}-${String(x.getDate()).padStart(2, '0')}`;
};

export function Client() {
  usePageTitle('Карточка клиента');
  const { id = '' } = useParams();
  const q = trpc.trainer.client.useQuery({ id });
  const feed = trpc.trainer.feed.useQuery({ clientId: id });
  const ev = useEvents(feed.data);
  const utils = trpc.useUtils();
  const nav = useNavigate();
  const [confirmDel, setConfirmDel] = useState(false);
  const [gp, setGp] = useState(false);
  const del = trpc.trainer.deleteClient.useMutation({
    onSuccess: () => {
      utils.trainer.clients.invalidate();
      utils.trainer.days.invalidate();
      utils.trainer.me.invalidate();
      toast('Клиент удалён');
      nav('/clients', { replace: true });
    },
    onError: (e) => toast(e.message || 'Не удалось удалить'),
  });
  const freeze = trpc.trainer.freezeClient.useMutation({
    onSuccess: (_r, v) => {
      utils.trainer.clients.invalidate();
      utils.trainer.client.invalidate({ id });
      utils.trainer.me.invalidate();
      utils.trainer.groups.invalidate();
      setConfirmDel(false);
      toast(v.frozen ? 'Клиент заморожен — место в тарифе освободилось' : 'Клиент снова активен');
    },
    onError: (e) => toast(e.message || 'Не получилось'),
  });
  const upd = trpc.trainer.updateClient.useMutation({
    onSuccess: () => {
      toast('Заметка сохранена');
      utils.trainer.client.invalidate({ id });
    },
    onError: (e) => toast(e.message || 'Не сохранилось'),
  });
  const loc = useLocation();
  const [tab, setTab] = useState<Tab>(() => tabOf(loc.hash.slice(1)));
  useEffect(() => setTab(tabOf(loc.hash.slice(1))), [loc.hash]);
  const go = (t: Tab) => {
    setTab(t);
    history.replaceState(null, '', '#' + t);
  };
  const [fee, setFee] = useState<null | 'pay' | 'edit'>(null);
  const C = q.data;

  if (q.error)
    return (
      <div className="pagewrap wide">
        <div className="empty">
          <div className="t">Клиент не найден</div>
          <div className="d">
            <Link to="/clients">К списку клиентов</Link>
          </div>
        </div>
      </div>
    );
  if (!C) return <div className="pagewrap"></div>;

  const T = today();
  const a = age(C.born);
  const la = lastActive(C);
  const since = C.trainingSince || new Date(C.createdAt).toISOString().slice(0, 10);
  const tgNick = (C.tg || '').replace('@', '');
  /* «Оплачено до» — ссылкой на таб оплаты */
  const paid = () => {
    const toPay = (e: React.MouseEvent) => (e.preventDefault(), go('pay'));
    if (!C.paidUntil)
      return (
        <a className="v" href="#pay" onClick={toPay}>
          <i className="dot"></i>не ведётся
        </a>
      );
    const r = daysBetween(T, C.paidUntil);
    const [cls, tx] = r < 0 ? ['rm', 'доступ закрыт'] : r === 0 ? ['warn', 'последний день'] : r === 1 ? ['warn', 'завтра'] : [r <= 7 ? 'warn' : 'ok', `ещё ${r} ${plural(r, 'день', 'дня', 'дней')}`];
    return (
      <a className={'v ' + cls} href="#pay" onClick={toPay}>
        <i className="dot"></i>
        {dshort(C.paidUntil)} <s>· {tx}</s>
      </a>
    );
  };


  return (
    <div className={'pagewrap' + (C.frozenAt ? ' cfrz' : '')}>
      <div className="phead" id="pagehead">{" "}</div>
      <div id="page">
        <LimitAlert clientId={C.id} />
        {C.frozenAt && (
          <div className="frzbar">
            <span>
              <b>Клиент заморожен с {dlong(isoDay(C.frozenAt))}</b>
              Не занимает место в тарифе. Тренировки, рекорды и переписка сохранены, но составлять и менять ничего нельзя.
            </span>
            <button className="btn" disabled={freeze.isPending} onClick={() => freeze.mutate({ id: C.id, frozen: false })}>
              Разморозить
            </button>
          </div>
        )}
        <div className="chead">
          <div className="chead-top">
            <span className="cph">
              {C.photo ? <img src={C.photo} alt="" /> : ini(C.name)}
              <button className="cph-up" title="Загрузить фото" onClick={() => toast('Загрузка фото появится позже — пока инициалы')}>
                <ICON.plus />
              </button>
            </span>
            <div className="cid">
              <div className="nm">{C.name}</div>
              <div className="sub">{[C.sex === 'f' ? 'Женщина' : C.sex === 'm' ? 'Мужчина' : '', a != null ? `${a} ${plural(a, 'год', 'года', 'лет')}` : ''].filter(Boolean).join(', ')}</div>
            </div>
            <div className="cacts">
              {C.overLimit || C.frozenAt ? null : (
                <Link className="btn" to={`/constructor?client=${C.id}&date=${T}`}>
                  <ICON.build /> В конструктор
                </Link>
              )}
              <Link className="btn gh" to={`/calendar?client=${C.id}`}>
                Календарь
              </Link>
              <button className="btn gh cdel" title="Удалить клиента" aria-label="Удалить клиента" onClick={() => setConfirmDel(true)}>
                <ICON.trash />
              </button>
            </div>
          </div>
          <div className="cinfo">
            <div className="cgrp">
              <div className="cgt">Связь</div>
              <div className="cf">
                <span className="k">Телефон</span>
                <a className="v" href={C.phone ? 'tel:' + C.phone.replace(/[^\d+]/g, '') : undefined} title="Позвонить">
                  <ICON.phone />
                  {phoneView(C.phone) || '—'}
                </a>
              </div>
              <div className="cf">
                <span className="k">Telegram</span>
                <a className="v" href={tgNick ? 'https://t.me/' + tgNick : undefined} target="_blank" rel="noopener" title="Написать в Telegram">
                  <ICON.tg />
                  {C.tg || '—'}
                </a>
              </div>
              <div className="cf">
                <span className="k">Max</span>
                <a
                  className="v"
                  href="#"
                  title="Написать в Max"
                  onClick={(e) => {
                    e.preventDefault();
                    toast('Max: ссылка на чат появится, когда мессенджер откроет диплинки');
                  }}
                >
                  <ICON.max />
                  {C.max || '—'}
                </a>
              </div>
            </div>
            <div className="cgrp">
              <div className="cgt">Тренировки</div>
              <div className="cf">
                <span className="k">Группа</span>
                <span className="cfv">
                  {C.group ? (
                    <Link className="v" to={'/groups?id=' + C.group.groupId} title="Открыть группу">
                      <ICON.grpSm />
                      {C.group.name}
                    </Link>
                  ) : (
                    <span className="v">
                      <i className="dot"></i>не состоит
                    </span>
                  )}
                  {!C.frozenAt && (
                    <button className="btn gh sm cfb" onClick={() => setGp(true)}>
                      {C.group ? (
                        'Сменить'
                      ) : (
                        <>
                          <ICON.plus /> В группу
                        </>
                      )}
                    </button>
                  )}
                </span>
              </div>
              <div className="cf">
                <span className="k">Оплачено до</span>
                <span className="cfv">
                  {paid()}
                  {C.paidUntil && (
                    <button className="btn gh sm cfb" onClick={() => setFee('pay')}>
                      <ICON.chk /> Принять оплату
                    </button>
                  )}
                </span>
              </div>
              <div className="cf">
                <span className="k">Активность</span>
                {!la ? (
                  <span className="v">
                    <i className="dot"></i>ещё не заходил
                  </span>
                ) : (
                  <span className={'v ' + (daysBetween(la, T) >= 3 ? 'warn' : 'ok')}>
                    <i className="dot"></i>
                    {ago(la)}
                  </span>
                )}
              </div>
              <div className="cf">
                <span className="k">С нами с</span>
                <span className="v">{dlong(since)}</span>
              </div>
            </div>
          </div>
          <label className="cnote">
            <span className="k">
              <ICON.chat /> Заметка тренера <i>· клиент не видит</i>
            </span>
            <NoteArea key={C.id + (C.trainerNote || '')} value={C.trainerNote || ''} onSave={(v) => upd.mutate({ id: C.id, trainerNote: v || null })} />
          </label>
        </div>

        <nav className="ctabs" id="ctabs">
          {TABS.map(([k, n]) => (
            <a
              key={k}
              href={'#' + k}
              className={tab === k ? 'on' : ''}
              onClick={(e) => {
                e.preventDefault();
                go(k);
              }}
            >
              {n}
            </a>
          ))}
        </nav>

        {tab === 'overview' ? (
          <>
            <section className="csec" id="meas">
              <div className="sechead">
                <span className="t">Замеры</span>
                <span className="ln"></span>
              </div>
              <Meas C={C} />
            </section>
            <section className="csec" id="pm">
              <div className="sechead">
                <span className="t">Рекорды</span>
                <span className="ln"></span>
              </div>
              <Maxes C={C} />
            </section>
          </>
        ) : tab === 'events' ? (
          <section className="csec" id="events">
            <div className="card cb flush cev">
              <div className="feed1" style={{ margin: 0 }}>
                {feed.data && <FeedList list={ev} />}
              </div>
            </div>
          </section>
        ) : (
          <section className="csec" id="pay">
            <div className="sechead">
              <span className="t">Оплата занятий</span>
              <span className="ln"></span>
              <span className="n">деньги клиент переводит вам сам — здесь срок и отметки</span>
            </div>
            <Pay C={C} onFee={setFee} />
          </section>
        )}
      </div>
      {fee && <FeeModal C={C} mode={fee} onClose={() => setFee(null)} />}
      {gp && <GroupPick client={{ id: C.id, name: C.name, groupId: C.group?.groupId ?? null }} onClose={() => setGp(false)} />}
      {confirmDel && (
        <Modal
          title={C.frozenAt ? 'Удалить клиента' : 'Заморозить или удалить?'}
          onClose={() => setConfirmDel(false)}
          foot={
            <>
              <button className="btn gh" onClick={() => setConfirmDel(false)}>
                Отмена
              </button>
              <span className="sp"></span>
              <button className="btn rm" disabled={del.isPending} onClick={() => del.mutate({ id: C.id })}>
                Удалить навсегда
              </button>
              {!C.frozenAt && (
                <button className="btn" disabled={freeze.isPending} onClick={() => freeze.mutate({ id: C.id, frozen: true })}>
                  Заморозить
                </button>
              )}
            </>
          }
        >
          <div className="delopts">
            {!C.frozenAt && (
              <div className="delopt">
                <b>Заморозить</b>
                <p>
                  Клиент перестанет занимать место в тарифе. Тренировки, результаты, рекорды и переписка сохранятся — их можно смотреть, но составлять новое нельзя{C.group ? ', из группы клиент выйдет' : ''}. Разморозить можно в любой момент, если в тарифе есть место.
                </p>
              </div>
            )}
            <div className="delopt rm">
              <b>Удалить навсегда</b>
              <p>
                Сотрётся всё: тренировки, результаты, замеры, рекорды и переписка.{C.userId ? ' Приложение клиента выйдет из аккаунта — вернуться к вам он сможет только по новой ссылке-приглашению, с чистого листа.' : ''} Отменить нельзя.
              </p>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
